export type ConsoleDiagnosticEntry = {
  ts: number;
  route: string;
  level: 'error' | 'unhandled' | 'rejection';
  message: string;
  stack?: string;
  count?: number;
};

const MAX_ENTRIES = 30;
const MAX_PER_MINUTE = 10;
const MESSAGE_LIMIT = 500;
const STACK_LIMIT = 1000;

let initialized = false;
let disabled = false;
let originalConsoleError: typeof console.error | null = null;
let originalOnError: OnErrorEventHandler | null = null;
let originalOnUnhandledRejection: ((this: Window, ev: PromiseRejectionEvent) => unknown) | null = null;
const entries: ConsoleDiagnosticEntry[] = [];
const captureTimes: number[] = [];

function safeString(value: unknown): string {
  try {
    if (value instanceof Error) return value.message || value.name;
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function stackFor(value: unknown): string | undefined {
  if (value instanceof Error && value.stack) return value.stack.slice(0, STACK_LIMIT);
  return undefined;
}

function currentRoute(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function shouldSkipCapture(): boolean {
  return window.location.pathname.includes('/api/bug-reports') || disabled;
}

function capture(level: ConsoleDiagnosticEntry['level'], message: unknown, stack?: string): void {
  if (shouldSkipCapture()) return;
  try {
    const now = Date.now();
    while (captureTimes.length && now - captureTimes[0] > 60_000) captureTimes.shift();
    if (captureTimes.length >= MAX_PER_MINUTE) return;
    captureTimes.push(now);

    const normalizedMessage = safeString(message).slice(0, MESSAGE_LIMIT);
    const normalizedStack = stack?.slice(0, STACK_LIMIT);
    const route = currentRoute();
    const stackTop = normalizedStack?.split('\n').find(Boolean) ?? '';
    const existing = entries.find(
      (entry) =>
        entry.route === route &&
        entry.message === normalizedMessage &&
        (entry.stack?.split('\n').find(Boolean) ?? '') === stackTop,
    );
    if (existing) {
      existing.ts = now;
      existing.count = (existing.count ?? 1) + 1;
      return;
    }
    entries.push({
      ts: now,
      route,
      level,
      message: normalizedMessage,
      stack: normalizedStack,
      count: 1,
    });
    while (entries.length > MAX_ENTRIES) entries.shift();
  } catch {
    disabled = true;
  }
}

export function initConsoleDiagnostics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  originalConsoleError = console.error.bind(console);
  originalOnError = window.onerror;
  originalOnUnhandledRejection = window.onunhandledrejection;

  console.error = (...args: unknown[]) => {
    capture('error', args.map(safeString).join(' '), args.map(stackFor).find(Boolean));
    originalConsoleError?.(...(args as Parameters<typeof console.error>));
  };

  window.onerror = (message, source, lineno, colno, error) => {
    capture('unhandled', message || error || 'Unhandled error', stackFor(error) ?? [source, lineno, colno].filter(Boolean).join(':'));
    if (typeof originalOnError === 'function') {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  window.onunhandledrejection = (event) => {
    capture('rejection', event.reason ?? 'Unhandled promise rejection', stackFor(event.reason));
    if (typeof originalOnUnhandledRejection === 'function') {
      return originalOnUnhandledRejection.call(window, event);
    }
    return undefined;
  };
}

export function getConsoleDiagnostics(): ConsoleDiagnosticEntry[] {
  return entries.map((entry) => ({ ...entry }));
}
